import React, { useState, useEffect } from 'react';
import { Plus, Settings, Users, Eye, EyeOff, UserPlus, Trash2, ShieldCheck, ChevronDown } from 'lucide-react';
import { DbClient } from '../lib/supabase';
import ClientService from '../services/clientService';
import AdminPanel from './AdminPanel';
import { ClientData } from '../types';
import { sampleClientData } from '../data/sampleData';
import AdminUserService, { AdminUser } from '../services/adminUserService';
import AuthService from '../services/authService';

interface AdminDashboardProps {
  onExitAdmin: () => void;
}

export default function AdminDashboard({ onExitAdmin }: AdminDashboardProps) {
  const [clients, setClients] = useState<DbClient[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreatePanel, setShowCreatePanel] = useState(false);
  const [editingClient, setEditingClient] = useState<ClientData | null>(null);
  const [adminUsers, setAdminUsers] = useState<AdminUser[]>([]);
  const [adminUserLoading, setAdminUserLoading] = useState(true);
  const [adminUserError, setAdminUserError] = useState<string | null>(null);
  const [newAdminUser, setNewAdminUser] = useState({ identifier: '', password: '', isAdmin: true, clientId: '' });
  const [creatingAdminUser, setCreatingAdminUser] = useState(false);
  const [updatingUserId, setUpdatingUserId] = useState<string | null>(null);
  const [showAdminLoginPanel, setShowAdminLoginPanel] = useState(false);

  useEffect(() => {
    loadClients();
    loadAdminUsers();
  }, []);

  useEffect(() => {
    if (!newAdminUser.isAdmin) {
      if (!newAdminUser.clientId && clients.length > 0) {
        setNewAdminUser(prev => ({ ...prev, clientId: clients[0].id }));
      }
    } else if (newAdminUser.clientId) {
      setNewAdminUser(prev => ({ ...prev, clientId: '' }));
    }
  }, [clients, newAdminUser.isAdmin, newAdminUser.clientId]);

  const loadAdminUsers = async () => {
    try {
      setAdminUserLoading(true);
      const list = await AdminUserService.list();
      setAdminUsers(list);
      setAdminUserError(null);
    } catch (error: any) {
      console.error('Failed to load admin users:', error);
      setAdminUserError(error?.message || 'Failed to load admin users.');
    } finally {
      setAdminUserLoading(false);
    }
  };

  const loadClients = async () => {
    try {
      const clientList = await ClientService.getAllClients();
      setClients(clientList);
    } catch (error) {
      console.error('Failed to load clients:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateClient = () => {
    setEditingClient({
      ...sampleClientData,
      id: '',
      name: '',
      slug: '',
      headline: '',
      introduction: '',
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    });
    setShowCreatePanel(true);
  };

  const handleEditClient = async (client: DbClient) => {
    try {
      const fullClientData = await ClientService.getClientBySlug(client.slug);
      if (fullClientData) {
        setEditingClient(fullClientData);
        setShowCreatePanel(true);
      }
    } catch (error) {
      console.error('Failed to load client data:', error);
    }
  };

  const handleSaveClient = async (clientData: ClientData): Promise<ClientData> => {
    try {
      if (clientData.id) {
        // Update existing client
        await ClientService.updateClient(clientData.id, clientData);
        if (clientData.uiSettings) {
          await ClientService.updateClientData(clientData.id, 'ui_settings', clientData.uiSettings);
        }
        if (clientData.pageContent) {
          await ClientService.updateClientData(clientData.id, 'page_content', clientData.pageContent);
        }
        if (clientData.demographics) {
          await ClientService.updateClientData(clientData.id, 'demographics', clientData.demographics);
        }
        if (clientData.personalValues) {
          await ClientService.updateClientData(clientData.id, 'personal_values', clientData.personalValues);
        }
        if (clientData.psychologicalDrivers) {
          await ClientService.updateClientData(clientData.id, 'psychological_drivers', clientData.psychologicalDrivers);
        }
        if (clientData.hobbiesAndHabits) {
          await ClientService.updateClientData(clientData.id, 'hobbies_habits', clientData.hobbiesAndHabits);
        }
        if (clientData.dailyRoutine) {
          await ClientService.updateClientData(clientData.id, 'daily_routine', clientData.dailyRoutine);
        }
        if (clientData.brandAffinity) {
          await ClientService.updateClientData(clientData.id, 'brand_affinity', clientData.brandAffinity);
        }
        if (clientData.customCharts) {
          await ClientService.updateClientData(clientData.id, 'custom_charts', clientData.customCharts);
        }
        if (clientData.flexibleSections) {
          await ClientService.updateClientData(clientData.id, 'flexible_sections', clientData.flexibleSections);
        }
        if (clientData.insights) {
          await ClientService.updateClientData(clientData.id, 'insights', clientData.insights);
        }
      } else {
        // Create new client
        const newClient = await ClientService.createClient(clientData);
        // Save the additional data
        if (clientData.demographics) {
          await ClientService.updateClientData(newClient.id, 'demographics', clientData.demographics);
        }
        if (clientData.personalValues) {
          await ClientService.updateClientData(newClient.id, 'personal_values', clientData.personalValues);
        }
        if (clientData.psychologicalDrivers) {
          await ClientService.updateClientData(newClient.id, 'psychological_drivers', clientData.psychologicalDrivers);
        }
        if (clientData.hobbiesAndHabits) {
          await ClientService.updateClientData(newClient.id, 'hobbies_habits', clientData.hobbiesAndHabits);
        }
        if (clientData.dailyRoutine) {
          await ClientService.updateClientData(newClient.id, 'daily_routine', clientData.dailyRoutine);
        }
        if (clientData.brandAffinity) {
          await ClientService.updateClientData(newClient.id, 'brand_affinity', clientData.brandAffinity);
        }
        if (clientData.pageContent) {
          await ClientService.updateClientData(newClient.id, 'page_content', clientData.pageContent);
        }
        if (clientData.uiSettings) {
          await ClientService.updateClientData(newClient.id, 'ui_settings', clientData.uiSettings);
        }
        if (clientData.customCharts) {
          await ClientService.updateClientData(newClient.id, 'custom_charts', clientData.customCharts);
        }
        if (clientData.flexibleSections) {
          await ClientService.updateClientData(newClient.id, 'flexible_sections', clientData.flexibleSections);
        }
        if (clientData.insights) {
          await ClientService.updateClientData(newClient.id, 'insights', clientData.insights);
        }
        clientData.id = newClient.id;
      }
      
      await loadClients();
      setShowCreatePanel(false);
      setEditingClient(null);
      const refreshed = clientData.id
        ? await ClientService.getClientBySlug(clientData.slug)
        : await ClientService.getClientBySlug(clientData.slug);
      return refreshed || clientData;
    } catch (error) {
      console.error('Failed to save client:', error);
      throw error;
    }
  };

  const handleDeleteClient = async (id: string) => {
    if (window.confirm('Are you sure you want to delete this client? This action cannot be undone.')) {
      try {
        await ClientService.deleteClient(id);
        await loadClients();
      } catch (error) {
        console.error('Failed to delete client:', error);
      }
    }
  };

  const handleCreateAdminUser = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newAdminUser.identifier || !newAdminUser.password) {
      setAdminUserError('Username or email and password are required.');
      return;
    }
    if (!newAdminUser.isAdmin && clients.length === 0) {
      setAdminUserError('Create a client before assigning non-admin users.');
      return;
    }
    if (!newAdminUser.isAdmin && !newAdminUser.clientId) {
      setAdminUserError('Select a client for non-admin users.');
      return;
    }
    try {
      setCreatingAdminUser(true);
      setAdminUserError(null);
      await AdminUserService.create(
        newAdminUser.identifier.trim(),
        newAdminUser.password,
        newAdminUser.isAdmin,
        newAdminUser.isAdmin ? null : newAdminUser.clientId
      );
      setNewAdminUser({ identifier: '', password: '', isAdmin: true, clientId: clients[0]?.id || '' });
      await loadAdminUsers();
    } catch (error: any) {
      console.error('Failed to create admin user:', error);
      setAdminUserError(error?.message || 'Failed to create admin user.');
    } finally {
      setCreatingAdminUser(false);
    }
  };

  const handleDeleteAdminUser = async (id: string) => {
    if (!window.confirm('Remove this admin login? This cannot be undone.')) return;
    try {
      setUpdatingUserId(id);
      await AdminUserService.delete(id);
      await loadAdminUsers();
    } catch (error: any) {
      console.error('Failed to delete admin user:', error);
      setAdminUserError(error?.message || 'Failed to delete admin user.');
    } finally {
      setUpdatingUserId(null);
    }
  };

  const handleAssignClient = async (userId: string, clientId: string | null) => {
    try {
      setUpdatingUserId(userId);
      await AdminUserService.update(userId, { clientId });
      await loadAdminUsers();
    } catch (error: any) {
      console.error('Failed to assign client:', error);
      alert(error?.message || 'Failed to assign client.');
    } finally {
      setUpdatingUserId(null);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading clients...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Admin Header */}
      <div className="bg-gray-800 text-white p-4">
        <div className="container mx-auto flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <div className="flex items-center">
              <Settings className="h-5 w-5 mr-2" />
              <h1 className="text-xl font-semibold">Admin Dashboard</h1>
            </div>
            <div className="text-sm text-gray-300">
              Littlefield Agency Client Management
            </div>
          </div>
          <button
            type="button"
            onClick={() => {
              AuthService.logout();
              onExitAdmin();
              window.location.href = 'https://data.littlefieldagency.com/la-admin';
            }}
            className="flex items-center text-gray-400 hover:text-white transition-colors"
          >
            <EyeOff className="h-4 w-4 mr-2" />
            Exit Admin
          </button>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8">
        {/* Header Actions */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Client Management</h2>
            <p className="text-gray-600">Manage client dashboards and data</p>
          </div>
          <button
            onClick={handleCreateClient}
            className="bg-teal-600 hover:bg-teal-700 text-white px-4 py-2 rounded-lg flex items-center transition-colors"
          >
            <Plus className="h-4 w-4 mr-2" />
            New Client
          </button>
        </div>

        {/* Admin user management */}
        <section className="bg-white border border-gray-200 rounded-xl shadow-sm p-6 mb-10">
          <button
            type="button"
            onClick={() => setShowAdminLoginPanel(prev => !prev)}
            className="w-full flex items-center justify-between text-left focus:outline-none"
            aria-expanded={showAdminLoginPanel}
          >
            <span className="flex items-center gap-2">
              <ShieldCheck className="h-5 w-5 text-emerald-600" />
              <span className="text-xl font-semibold text-gray-900">Admin Logins</span>
            </span>
            <ChevronDown
              className={`h-5 w-5 text-gray-500 transition-transform duration-200 ${showAdminLoginPanel ? 'rotate-180' : ''}`}
            />
          </button>

          {showAdminLoginPanel && (
            <div className="mt-6">
              <p className="text-sm text-gray-600 mb-6">
                Create admin or read-only logins. Passwords are stored as secure hashes.
              </p>

              <form onSubmit={handleCreateAdminUser} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 items-end mb-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Username or Email
                  </label>
                  <input
                    type="text"
                    value={newAdminUser.identifier}
                    onChange={(e) => setNewAdminUser(prev => ({ ...prev, identifier: e.target.value }))}
                    className="w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-emerald-500"
                    placeholder="e.g. alex or alex@example.com"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Password
                  </label>
                  <input
                    type="password"
                    value={newAdminUser.password}
                    onChange={(e) => setNewAdminUser(prev => ({ ...prev, password: e.target.value }))}
                    className="w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-emerald-500"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Assigned Client
                  </label>
                  <select
                    value={newAdminUser.clientId}
                    onChange={(e) => setNewAdminUser(prev => ({ ...prev, clientId: e.target.value }))}
                    className="w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-emerald-500"
                    disabled={newAdminUser.isAdmin}
                  >
                    <option value="">None</option>
                    {clients.map((client) => (
                      <option key={client.id} value={client.id}>{client.name}</option>
                    ))}
                  </select>
                  <p className="mt-1 text-xs text-gray-500">Required for non-admin logins.</p>
                </div>
                <div className="flex items-center mt-1">
                  <label className="inline-flex items-center gap-2 text-sm text-gray-700">
                    <input
                      type="checkbox"
                      checked={newAdminUser.isAdmin}
                      onChange={(e) => setNewAdminUser(prev => ({
                        ...prev,
                        isAdmin: e.target.checked,
                        clientId: e.target.checked ? '' : (prev.clientId || clients[0]?.id || '')
                      }))}
                      className="h-4 w-4 text-emerald-600 border-gray-300 rounded"
                    />
                    Full Admin Access
                  </label>
                </div>
                <div>
                  <button
                    type="submit"
                    disabled={creatingAdminUser}
                    className="inline-flex items-center justify-center w-full bg-emerald-600 hover:bg-emerald-700 text-white font-semibold px-4 py-2 rounded transition-colors disabled:opacity-50"
                  >
                    {creatingAdminUser ? (
                      <>
                        <div className="h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                        Saving...
                      </>
                    ) : (
                      <>
                        <UserPlus className="h-4 w-4 mr-2" />
                        Add Login
                      </>
                    )}
                  </button>
                </div>
              </form>

              {adminUserError && (
                <div className="text-sm text-red-600 bg-red-50 border border-red-100 rounded px-4 py-2 mb-4">
                  {adminUserError}
                </div>
              )}

              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200 text-sm">
                  <thead>
                    <tr className="text-left text-gray-600">
                      <th className="py-2 pr-4">Email</th>
                      <th className="py-2 pr-4">Privileges</th>
                      <th className="py-2 pr-4">Assigned Client</th>
                      <th className="py-2 pr-4">Created</th>
                      <th className="py-2 pr-4">Last Login</th>
                      <th className="py-2 pr-4 text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {adminUserLoading ? (
                      <tr>
                        <td colSpan={6} className="py-4 text-center text-gray-500">
                          Loading admin users...
                        </td>
                      </tr>
                    ) : adminUsers.length === 0 ? (
                      <tr>
                        <td colSpan={6} className="py-4 text-center text-gray-500">
                          No admin users created yet.
                        </td>
                      </tr>
                    ) : (
                      adminUsers.map(user => (
                        <tr key={user.id}>
                          <td className="py-3 pr-4 text-gray-900">{user.email || user.username}</td>
                          <td className="py-3 pr-4">
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${user.isAdmin ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-600'}`}>
                              {user.isAdmin ? 'Admin' : 'Viewer'}
                            </span>
                          </td>
                          <td className="py-3 pr-4">
                            <div className="flex items-center gap-2">
                              <select
                                value={user.clientId || ''}
                                onChange={(e) => handleAssignClient(user.id, e.target.value || null)}
                                disabled={user.isAdmin || updatingUserId === user.id}
                                className="px-2 py-1 border rounded text-sm"
                              >
                                <option value="">None</option>
                                {clients.map((client) => (
                                  <option key={client.id} value={client.id}>{client.name}</option>
                                ))}
                              </select>
                              {!user.isAdmin && user.client?.slug ? (
                                <a href={`/${user.client.slug}`} className="text-xs text-teal-600 hover:text-teal-700">
                                  {user.client.name || user.client.slug}
                                </a>
                              ) : null}
                              {updatingUserId === user.id && (
                                <div className="h-3 w-3 border-2 border-teal-500 border-t-transparent rounded-full animate-spin" />
                              )}
                            </div>
                          </td>
                          <td className="py-3 pr-4 text-gray-500">
                            {user.created_at ? new Date(user.created_at).toLocaleDateString() : '—'}
                          </td>
                          <td className="py-3 pr-4 text-gray-500">
                            {user.last_login ? new Date(user.last_login).toLocaleString() : 'Never'}
                          </td>
                          <td className="py-3 pr-0 text-right">
                            <button
                              type="button"
                              onClick={() => handleDeleteAdminUser(user.id)}
                              className="inline-flex items-center text-xs text-red-500 hover:text-red-600"
                            >
                              <Trash2 className="h-4 w-4 mr-1" />
                              Remove
                            </button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </section>

        {/* Clients Grid */}
        {clients.length === 0 ? (
          <div className="text-center py-12">
            <Users className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No clients yet</h3>
            <p className="text-gray-600 mb-4">Get started by creating your first client dashboard</p>
            <button
              onClick={handleCreateClient}
              className="bg-teal-600 hover:bg-teal-700 text-white px-4 py-2 rounded-lg inline-flex items-center"
            >
              <Plus className="h-4 w-4 mr-2" />
              Create First Client
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {clients.map((client) => (
              <div key={client.id} className="bg-white rounded-lg shadow-md overflow-hidden">
                <div className="p-6">
                  {client.logo_url && (
                    <img
                      src={client.logo_url}
                      alt={`${client.name} Logo`}
                      className="h-12 w-auto mb-4"
                    />
                  )}
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">{client.name}</h3>
                  <p className="text-sm text-gray-600 mb-4 line-clamp-2">{client.headline}</p>
                  <div className="flex items-center justify-between text-xs text-gray-500 mb-4">
                    <span>/{client.slug}</span>
                    <span>{new Date(client.updated_at).toLocaleDateString()}</span>
                  </div>
                </div>
                <div className="px-6 py-3 bg-gray-50 flex items-center justify-between">
                  <a
                    href={`/${client.slug}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-teal-600 hover:text-teal-700 text-sm flex items-center"
                  >
                    <Eye className="h-4 w-4 mr-1" />
                    View
                  </a>
                  <div className="flex space-x-2">
                    <button
                      onClick={() => handleEditClient(client)}
                      className="text-gray-600 hover:text-gray-800 text-sm"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleDeleteClient(client.id)}
                      className="text-red-600 hover:text-red-800 text-sm"
                    >
                      Delete
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Admin Panel Modal */}
      {showCreatePanel && editingClient && (
        <AdminPanel
          clientData={editingClient}
          onSave={handleSaveClient}
          onClose={() => {
            setShowCreatePanel(false);
            setEditingClient(null);
          }}
        />
      )}
    </div>
  );
}
